import clsx from "clsx";
import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import { useBoolean, useCopyToClipboard } from "usehooks-ts";

import { CourseRole, MaxRole, OrgRole, SectionRole } from "../../../../../shared/types/roles";
import { AddUserModal } from "../AddUserModal";
import { BulkImportMenu } from "../bulk-import/BulkImportMenu";

import { AdminUserOptionsMenu } from "./AdminUserOptionsMenu";
import { ManageCourseAdminModal } from "./ManageCourseAdminModal";
import { OrgRoleIconWithTooltip } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { serverManageCourseAdmin } from "api/api-server-actions";
import { EditOrgUserModal } from "components/modals/EditOrgUserModal";
import type { BulkImportType, OrgUserType, UserType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TR } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { ArrowDownToLineIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  courseGroupingUserSelectors,
  courseUserSelectors,
  orgUserSelectors,
  sectionSelectors,
  selectCurrentOrg,
  selectEnabledSectionUsersByRole,
  selectIsEditModeEnabled,
  userSelectors,
} from "store/selectors";
import { selectUserInviteStatuses } from "store/selectors/computed";
import { sortByFirstName } from "store/selectors/sorting";
import { useListSelector } from "store/store-hooks";
import { toastErrorMessage, toastLocalizedOperationCatcher } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateOrgUsersCSV } from "utils/reporting";
import { getSectionResourceRole } from "utils/section";
import { useFetchUserInviteStatuses } from "utils/user-invite-status";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

type AdminUserEntryType = OrgUserType &
  UserType & {
    org_user_id: string;
    tooltipSectionRoleTable: ReactNode;
    invite_status: InviteStatus;
    maxRole: MaxRole;
    is_course_admin: boolean;
  };

const ORG_ROLE_SORT_ORDER: Record<OrgRole | CourseRole, number> = {
  admin: 0,
  course_admin: 1,
  other: 2,
  none: 3,
  recording_agent: 4, // filtered out above; here for exhaustiveness
};

const MAX_ROLE_SORT_ORDER: Record<MaxRole, number> = {
  super_admin: 0,
  org_admin: 1,
  course_admin: 2,
  instructor: 3,
  section_assistant: 4,
  student: 5,
};

function getMaxRole(
  orgUser: OrgUserType,
  user: UserType,
  sectionUsersForUser: { role: SectionRole }[],
  courseUsersForUser: { role: CourseRole }[],
  courseGroupingUsers: { role: CourseRole }[],
): MaxRole {
  if (user.is_superuser) return MaxRole.SUPER_ADMIN;

  if (orgUser.role === OrgRole.ADMIN) return MaxRole.ORG_ADMIN;

  const hasCourseAdminForGrouping = courseGroupingUsers.some(
    (cu) => cu.role === CourseRole.COURSE_ADMIN,
  );
  if (hasCourseAdminForGrouping) return MaxRole.COURSE_ADMIN;

  const hasCourseAdmin = courseUsersForUser.some((cu) => cu.role === CourseRole.COURSE_ADMIN);
  if (hasCourseAdmin) return MaxRole.COURSE_ADMIN;

  const hasInstructor = sectionUsersForUser.some((cu) => cu.role === SectionRole.INSTRUCTOR);
  if (hasInstructor) return MaxRole.INSTRUCTOR;

  const hasSectionAssistant = sectionUsersForUser.some(
    (cu) => cu.role === SectionRole.SECTION_ASSISTANT,
  );
  if (hasSectionAssistant) return MaxRole.SECTION_ASSISTANT;

  return MaxRole.STUDENT;
}

type SortField = "name" | "email" | "maxRole" | "external_id" | "invite_status";

interface AdminUsersListProps {
  onShowBulkImportModal: (importType: BulkImportType) => void;
}

export const AdminUsersList: FC<AdminUsersListProps> = ({ onShowBulkImportModal }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const dispatch = useAppDispatch();
  const allUsers = useListSelector(userSelectors.selectAll);
  const orgUsers = useAppSelector(orgUserSelectors.selectAll);
  const userInviteStatuses = useAppSelector(selectUserInviteStatuses);
  const courseUsers = useAppSelector(courseUserSelectors.selectAll);
  const sectionUsers = useListSelector(selectEnabledSectionUsersByRole);
  const courseGroupingUsers = useAppSelector(courseGroupingUserSelectors.selectAll);
  const sections = useListSelector(sectionSelectors.selectAll);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorOrg, canImpersonateUser, canSeeAllOrgUsers } = useCapabilities();
  const editModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const canToggleDisabled = canAuthorOrg;
  const canChangeOrgRole = canAuthorOrg;
  const isSmallScreen = !useIsMdOrLarger();

  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    storeApi.section_users.list({});
    storeApi.course_users.list({});
    storeApi.course_grouping_users.list({});
  }, []);

  useFetchUserInviteStatuses();

  // OrgUsers and SectionUsers are both links to the User model, and to Org and Section separately,
  // and both have a "role" string attached.
  // In this view we'd like to show a list of all users that have an OrgUser, their role, and the
  // amount of SectionUsers that they have.

  const userList: AdminUserEntryType[] = orgUsers
    .flatMap((orgUser) => {
      const user = allUsers.find((u) => u.id === orgUser.user_id);
      if (!user || orgUser.role === OrgRole.RECORDING_AGENT) {
        return [];
      }

      if (!canSeeAllOrgUsers && user.is_superuser) {
        return [];
      }

      const sectionUsersForUser = sectionUsers.filter((su) => su.user_id === orgUser.user_id);
      const courseUsersForUser = courseUsers.filter((cu) => cu.user_id === orgUser.user_id);
      const courseGroupingUsersForUser = courseGroupingUsers.filter(
        (cgu) => cgu.user_id === orgUser.user_id,
      );

      const tooltipSectionRoleTable = sectionUsersForUser.map((cu) => {
        const section = sections.find((s) => s.id === cu.section_id);
        const role = getSectionResourceRole(cu.role);

        return (
          <div key={cu.id}>
            <strong>{section?.title}</strong>: {t(`roles.section_${role}`)}
          </div>
        );
      });

      const maxRole = getMaxRole(
        orgUser,
        user,
        sectionUsersForUser,
        courseUsersForUser,
        courseGroupingUsersForUser,
      );

      return [
        {
          ...orgUser,
          ...user,
          org_user_id: orgUser.id,
          tooltipSectionRoleTable,
          invite_status: userInviteStatuses[user.id] || InviteStatus.NOT_SENT,
          maxRole,
          is_course_admin: courseUsersForUser.length > 0 || courseGroupingUsersForUser.length > 0,
          role: orgUser.role,
        },
      ];
    })
    .toSorted((a, b) => {
      const aRole =
        a.role === OrgRole.OTHER
          ? a.is_course_admin
            ? CourseRole.COURSE_ADMIN
            : OrgRole.OTHER
          : a.role;
      const bRole =
        b.role === OrgRole.OTHER
          ? b.is_course_admin
            ? CourseRole.COURSE_ADMIN
            : OrgRole.OTHER
          : b.role;
      const roleDiff = ORG_ROLE_SORT_ORDER[aRole] - ORG_ROLE_SORT_ORDER[bRole];
      return roleDiff !== 0 ? roleDiff : sortByFirstName(a, b);
    });

  const canSeeExternalUserIds = canAuthorOrg && userList.some((u) => u.external_user_id);

  const getOrgUserSortValue = useCallback(
    (entry: AdminUserEntryType, field: SortField): number | string | null => {
      switch (field) {
        case "name":
          return `${(entry.first_name ?? "").toLowerCase()} ${(entry.last_name ?? "").toLowerCase()}`.trim();
        case "email":
          return entry.email ?? "";
        case "maxRole":
          return MAX_ROLE_SORT_ORDER[entry.maxRole];
        case "external_id":
          return entry.external_user_id ?? "";
        case "invite_status":
          return entry.invite_status;
        default:
          return null;
      }
    },
    [],
  );

  const onChangeRole = async (newRole: OrgRole | CourseRole, user: AdminUserEntryType) => {
    if (newRole === CourseRole.COURSE_ADMIN) {
      manageCourseAdmin(user);
      return;
    }

    if (user.is_course_admin) {
      await serverManageCourseAdmin(user.id, []);
      const courseUsersForUser = courseUsers.filter((cu) => cu.user_id === user.id);
      courseUsersForUser.forEach((cu) =>
        dispatch(slices.course_users.actions.removeOne({ id: cu.id })),
      );

      const courseGroupingUsersForUser = courseGroupingUsers.filter(
        (cgu) => cgu.user_id === user.id,
      );
      courseGroupingUsersForUser.forEach((cgu) =>
        dispatch(slices.course_grouping_users.actions.removeOne({ id: cgu.id })),
      );
    }

    storeApi.org_users
      .partial_update({
        id: user.org_user_id,
        role: newRole,
      })
      .catch(toastLocalizedOperationCatcher("update_user_role"));
  };

  const onImpersonate = async (user: { id: string }) => {
    const url = await createImpersonationUrl(user.id);

    if (url) {
      copyToClipboard(url);
    } else {
      toastErrorMessage(t("error.toasts.failed_to_impersonate_user"));
    }
  };

  const manageCourseAdmin = (user: AdminUserEntryType) => {
    showConfirmationModal({
      title: t("user_options_menu.manage_course_admin.title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <ManageCourseAdminModal
          userId={user.id}
          userName={getUserFullName(user)}
          onClose={onClose}
        />
      ),
    });
  };

  const onToggleDisabled = (user: { org_user_id: string; is_disabled: boolean }) => {
    storeApi.org_users.partial_update({
      id: user.org_user_id,
      is_disabled: !user.is_disabled,
    });
  };

  const onDownloadCSV = () => {
    if (!currentOrg) {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
      return;
    }
    if (userList.length === 0) {
      toastErrorMessage(t("section_enrollment_report.no_data"));
      return;
    }
    try {
      const { csvData, filename } = generateOrgUsersCSV(
        userList,
        currentOrg.name,
        canSeeExternalUserIds,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
    }
  };

  return (
    <div className="h-full overflow-y-auto pb-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="mb-0 hidden md:block">{t("user_options_menu.org_sidebar_title")}</h3>
        <div className={clsx("flex gap-2", isSmallScreen && "w-full")}>
          <SquareButton
            aria-label={t("admin.add_user.add_user")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Add new"
            kind="tertiary"
            size="xs"
            onClick={() => setShowAddUserModal(true)}
          >
            {t("admin.add_user.add_user")}
          </SquareButton>

          <BulkImportMenu disabled={!editModeEnabled} onSelectImportType={onShowBulkImportModal} />

          <SquareButton
            aria-label={t("user_options_menu.download_csv")}
            className="flex-1 md:flex-none"
            disabled={userList.length === 0}
            eventName="Org Users - Download CSV"
            kind="tertiary"
            size="xs"
            onClick={onDownloadCSV}
          >
            <IconText
              iconStart={<ArrowDownToLineIcon />}
              text={t("user_options_menu.download_csv")}
            />
          </SquareButton>
        </div>
      </div>

      <ScrollableTableContainer className="mb-0">
        <SortableTableWrapper<AdminUserEntryType, SortField>
          data={userList}
          getSortValue={getOrgUserSortValue}
        >
          <SortableTable>
            <thead>
              <tr>
                <SortableHeader field="name" label={t("user_options_menu.table_header_name")} />
                <SortableHeader field="email" label={t("user_options_menu.table_header_email")} />
                <SortableHeader
                  field="maxRole"
                  label={t("user_options_menu.table_header_max_role")}
                />
                {canSeeExternalUserIds && (
                  <SortableHeader
                    field="external_id"
                    label={t("user_options_menu.table_header_external_id")}
                  />
                )}
                <SortableHeader
                  field="invite_status"
                  label={t("user_options_menu.table_header_invite_status")}
                />
              </tr>
            </thead>

            <SortableBody<AdminUserEntryType>>
              {(sortedData) =>
                sortedData.map((user) => (
                  <AdminUserEntry
                    key={user.id}
                    showExternalId={canSeeExternalUserIds}
                    user={user}
                    onChangeRole={canChangeOrgRole ? onChangeRole : undefined}
                    onImpersonate={canImpersonateUser ? onImpersonate : undefined}
                    onToggleDisabled={canToggleDisabled ? onToggleDisabled : undefined}
                  />
                ))
              }
            </SortableBody>
          </SortableTable>
        </SortableTableWrapper>
      </ScrollableTableContainer>
      {showAddUserModal && <AddUserModal onClose={() => setShowAddUserModal(false)} />}
    </div>
  );
};

type AdminUserEntryProps = {
  user: AdminUserEntryType;
  onChangeRole?: (newRole: OrgRole | CourseRole, user: AdminUserEntryType) => void;
  onImpersonate?: (user: { id: string }) => void;
  onToggleDisabled?: (user: { org_user_id: string; is_disabled: boolean }) => void;
  showExternalId?: boolean;
};

export const AdminUserEntry: FC<AdminUserEntryProps> = ({
  user,
  onChangeRole,
  onImpersonate,
  onToggleDisabled,
  showExternalId = false,
}) => {
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean(false);
  const hasActions = Boolean(onChangeRole || onImpersonate || onToggleDisabled);
  return (
    <TR className="items-center justify-between">
      <TD className={clsx(user.is_disabled && "italic text-black-tint-70")}>
        <div className="flex items-center gap-1">
          <OrgRoleIconWithTooltip
            extraContent={user.tooltipSectionRoleTable}
            role={user.is_superuser ? "superuser" : user.role}
          />

          <span className="max-w-m truncate" title={getUserFullName(user)}>
            {getUserFullName(user)}
          </span>
        </div>
      </TD>

      <TD className="body-xs relative">{user.email}</TD>

      <TD className="body-xs relative">{t(`user_options_menu.max_role.${user.maxRole}`)}</TD>

      {showExternalId && <TD className="body-xs relative">{user.external_user_id || "-"}</TD>}

      <TD className="body-xs flex items-center justify-between">
        <span>{t(`invite_status.${user.invite_status}`)}</span>
        {hasActions && (
          <div className="">
            <AdminUserOptionsMenu
              currentRole={user.is_superuser ? "superuser" : user.role}
              user={user}
              onChangeRole={onChangeRole ? (newRole) => onChangeRole(newRole, user) : undefined}
              onEdit={openModal}
              onImpersonate={onImpersonate ? () => onImpersonate(user) : undefined}
              onToggleDisabled={onToggleDisabled ? () => onToggleDisabled(user) : undefined}
            />
          </div>
        )}

        <EditOrgUserModal open={isModalOpen} user={user} onClose={closeModal} />
      </TD>
    </TR>
  );
};
